import React, { useEffect, useState } from "react";
import moment from "moment";
import "moment-timezone";
import Box from "@mui/system/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import TableSortLabel from "@mui/material/TableSortLabel";
import EnhancedTableToolbar from "../../../common/table/EnhancedTableToolbar";
import { exportTempList } from "../../../config/app-constants";
import ExportTemplateDialog from "./ExportTemplateDialog";
import SanitizeTemplateDialog from "./SanitizeTemplateDialog";
import DownloadConfirmDialog from "./DownloadConfirmDialog";
import Sortswitch from "../../../../assets/images/Sortswitch.png";
import Preloader from "../../../helpers/Preloader";
import { apiCall, apiCallPaginated } from "../../../utils/action";
import { customSort } from "../../../utils/services";
import {
  downloadCommentLibraryZip,
  downloadTemplateJson,
  downloadTemplatesZip,
} from "../../../utils/companyExport";

function ExportTemplateList(props) {
  const { showTemplateList, company_key, isHtCompany } = props;
  const [state, setState] = useState({
    showLoader: false,
    downloadTarget: null,
    openDialog: null,
    openSanitizeDialog: null,
    rowsPerPage: 2,
    page: 0,
    totalListCount: 0,
    templateList: null,
    selectedCompany: null,
    excelData: null,
    apiPageCount: 0,
    order: "asc",
    orderBy: "company_key",
  });

  const {
    showLoader,
    downloadTarget,
    searchText,
    rowsPerPage,
    page,
    totalListCount,
    fileteredList,
    excelData,
    apiPageCount,
    order,
    orderBy,
    openDialog,
    templateList,
    openSanitizeDialog,
    openCommentSanitize
  } = state;
  let result = JSON.parse(JSON.stringify(excelData));

  const getTemplateList = (searchText = null) => {
    const formData = {
      parent_app_id: showTemplateList,
      search: searchText,
    };
    apiCallPaginated(
      (response) => {
        if (response.data.success) {
          setState((prevState) => ({
            ...prevState,
            showLoader: false,
            page: response?.data?.data?.current_page - 1,
            totalListCount: response?.data?.data?.total,
            rowsPerPage: response?.data?.data?.per_page,
            templateList: response?.data?.data?.data,
            fileteredList: response?.data?.data?.data,
            excelData: response?.data?.data?.data,
          }));
        } else {
          if (response.data.status_code === 404)
            setState((prevState) => ({
              ...prevState,
              templateList: [],
              fileteredList: [],
              showLoader: false,
            }));
        }
      },
      formData,
      "GET_PUBLISHED_TEMPLATE_LIST",
      apiPageCount + 1
    );
  };

  useEffect(() => {
    async function callApi() {
      setState((prevState) => ({ ...prevState, showLoader: true }));
      await getTemplateList();
    }
    if (showTemplateList) callApi();
  }, [apiPageCount, rowsPerPage]);

  const handleOpenDialog = (obj) => {
    setState((prevState) => ({ ...prevState, openDialog: obj?.id }));
  };
  const handleOpenSanitizeDialog = (obj) => {
    setState((prevState) => ({ ...prevState, openSanitizeDialog: obj?.id }));
  };
  const handleCloseDialog = () => {
    setState((prevState) => ({ ...prevState, openDialog: null }));
  };
  const handleCloseSanitizeDialog = () => {
    setState((prevState) => ({ ...prevState, openSanitizeDialog: null }));
  };
  // const handleSearchEnter = (event) => {
  //   let searchQuery = event.target.value.toLowerCase();
  //   let searchData = templateList?.filter(
  //     (ele) =>
  //       ele.name?.toLowerCase()?.includes(searchQuery) ||
  //       ele.active?.toLowerCase()?.includes(searchQuery)
  //   );
  //   if (searchQuery !== "") {
  //     if (searchData?.length !== 0) {
  //       setState((prevState) => ({
  //         ...prevState,
  //         fileteredList: searchData,
  //         totalListCount: 0,
  //       }));
  //     } else setState((prevState) => ({ ...prevState, fileteredList: [] }));
  //   } else
  //     setState((prevState) => ({
  //       ...prevState,
  //       fileteredList: templateList,
  //     }));
  // };

  const openDownloadDialog = (type, template = null) =>
    setState((prevState) => ({ ...prevState, downloadTarget: { type, template } }));

  const handleCloseDownloadDialog = () =>
    setState((prevState) => ({ ...prevState, downloadTarget: null }));

  const fetchAllTemplateIds = () =>
    new Promise((resolve, reject) => {
      apiCall(
        (response) => {
          const templateIds = response?.data?.data;
          if (response?.data?.success && Array.isArray(templateIds)) {
            resolve(templateIds);
          } else {
            reject(new Error(response?.data?.message || "Unable to fetch templates."));
          }
        },
        { parent_app_id: showTemplateList },
        "EXPORT_TEMPLATE_IDS"
      );
    });

  const downloadAllTemplates = async () => {
    const templateIds = await fetchAllTemplateIds();
    if (!templateIds.length) throw new Error("No templates found to download.");
    await downloadTemplatesZip(showTemplateList, templateIds, company_key);
  };

  const downloadDialogConfig = {
    commentLibrary: {
      title: "Download Comment Library",
      message: "Download the full comment library for this company?",
      onConfirm: () => downloadCommentLibraryZip(showTemplateList, company_key, isHtCompany),
    },
    allTemplates: {
      title: "Download All Templates",
      message: "Download all templates for this company?",
      onConfirm: downloadAllTemplates,
    },
    template: {
      title: "Download Template",
      message: `Download template "${downloadTarget?.template?.name ?? ""}"?`,
      onConfirm: () => downloadTemplateJson(showTemplateList, downloadTarget?.template?.id),
    },
  }[downloadTarget?.type];

  const handleSearchFilter = (event) => {
    getTemplateList(event.target.value);
    setState((prevState) => ({
      ...prevState,
      searchText: event.target.value,
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setState((prevState) => ({
      ...prevState,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    }));
  };

  const handleChangePage = (event, newPage) => {
    setState((prevState) => ({
      ...prevState,
      page: newPage,
      apiPageCount: newPage,
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    setState((prevState) => ({
      ...prevState,
      page: 0,
      apiPageCount: 0,
      rowsPerPage: parseInt(event.target.value, 10),
    }));
  };

  return (
    <Box px={10} pt={3} pb={10}>
      <Preloader showLoader={showLoader} />
      <DownloadConfirmDialog
        open={Boolean(downloadTarget)}
        title={downloadDialogConfig?.title}
        message={downloadDialogConfig?.message}
        onConfirm={downloadDialogConfig?.onConfirm}
        handleCloseDialog={handleCloseDownloadDialog}
      />
      <ExportTemplateDialog
        openDialog={openDialog}
        handleCloseDialog={handleCloseDialog}
        showTemplateList={showTemplateList}
      />
      <SanitizeTemplateDialog
        openDialog={openSanitizeDialog}
        handleCloseDialog={handleCloseSanitizeDialog}
        showTemplateList={showTemplateList}
        company_key={company_key}
      />
      <Grid container className="headingGrid">
        <Typography className="importHeading">Import</Typography>
        <Box display="flex" gap={2} ml="auto">
          <Tooltip title="Downloads this company's full comment library as a single zip containing separate JSON files for comments, lists, tags, and either summaries or ratings, depending on the company type.">
            <span>
              <Button
                id="downloadCommentLibraryBtn"
                variant="outlined"
                onClick={() => openDownloadDialog("commentLibrary")}
              >
                Download Comment Library
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Downloads this company's templates, each as its own JSON file, in a single zip.">
            <span>
              <Button
                id="downloadAllTemplatesBtn"
                variant="outlined"
                onClick={() => openDownloadDialog("allTemplates")}
              >
                Download All Templates
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Grid>
      <Paper
        sx={{ width: "100%", sm: 8, lg: 2, mt: "18px" }}
        className="teams-table-wrapper"
      >
        <EnhancedTableToolbar
          displayFilters={false}
          searchIcon={true}
          search={searchText}
          isMobile={false}
          handleSearchFilter={handleSearchFilter}
          // handleSearchEnter={handleSearchEnter}
          excelData={result}
          excelName={"Import Company List"}
          showTemplateList={showTemplateList}
          setState={setState}
        />
        {!fileteredList ? null : fileteredList?.length ? (
          <>
            <TableContainer className="ptCompanyListTable">
              <Table>
                <TableHead>
                  <TableRow>
                    {exportTempList.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        className="ptHeadingTitle"
                        align={headCell.id === "export" ? "center" : ""}
                      >
                        <TableSortLabel
                          IconComponent={() =>
                            headCell.name !== "Actions" && (
                              <img
                                src={Sortswitch}
                                alt="sort-icon"
                                className="sort-switch"
                              />
                            )
                          }
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={() => handleRequestSort(headCell.id)}
                          sx={{
                            marginBottom: "10.5px",
                            fontSize: "12px",
                            flexDirection: "row-reverse",
                          }}
                        >
                          {headCell.name}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customSort(fileteredList, order, orderBy).map(
                    (obj, index) => (
                      <TableRow key={index} className="ptRowColor">
                        <TableCell className="ptCell">
                          <Typography className="ptCompanyDetails">
                            {obj?.name}
                          </Typography>
                        </TableCell>
                        <TableCell className="ptCell">
                          <Typography className="ptCompanyDetails">
                            {obj?.description}
                          </Typography>
                        </TableCell>
                        <TableCell className="ptCell">
                          {obj?.updated_at ? (
                            <Grid container>
                              <Grid item xs={12}>
                                <Typography className="ptCompanyDetails">
                                  {moment(new Date(obj?.updated_at)).format(
                                    "MM/DD/YY"
                                  )}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography
                                  fontSize={10}
                                  className="dateTimeColor"
                                >
                                  {moment(new Date(obj?.updated_at)).fromNow()}
                                </Typography>
                              </Grid>
                            </Grid>
                          ) : null}
                        </TableCell>
                        <TableCell className="ptCell">
                          <Button
                            id={`exportTemplateBtn${index}`}
                            onClick={() => handleOpenDialog(obj)}
                            className="ptBeginImportButton"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Export Template
                          </Button>
                          <br />
                          <Button
                            id={`sanitizeTemplateBtn${index}`}
                            onClick={() => handleOpenSanitizeDialog(obj)}
                            className="ptBeginImportButton"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Sanitize Template
                          </Button>
                          <br />
                          <Button
                            id={`downloadTemplateBtn${index}`}
                            onClick={() => openDownloadDialog("template", obj)}
                            className="ptBeginImportButton"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            Download Template
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className="company-pagination"
              rowsPerPageOptions={[10, 20, 30]}
              component="div"
              count={totalListCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        ) : (
          <Paper
            sx={{ width: "100%", sm: 8, mb: 4, lg: 2, mt: 10 }}
            elevation={0}
            className={"inspection-table-main"}
          >
            <Alert severity="error">No datas found</Alert>
          </Paper>
        )}
      </Paper>
    </Box>
  );
}

export default ExportTemplateList;
